import { useState, type FormEvent } from "react";
import { shortDate } from "./format";
import {
  useCreateHorizon,
  useHorizonDetail,
  useHorizons,
  useSpaceParam,
  useSpaces,
  useUpdateHorizon,
} from "./hooks";
import type { Horizon, HorizonInput, HorizonLevel, HorizonStatus } from "./types";

const LEVELS: { level: HorizonLevel; title: string; singular: string }[] = [
  { level: 2, title: "Obszary odpowiedzialności", singular: "Obszar odpowiedzialności" },
  { level: 3, title: "Cele", singular: "Cel" },
  { level: 4, title: "Wizja", singular: "Wizja" },
  { level: 5, title: "Cel życiowy i zasady", singular: "Cel życiowy lub zasada" },
];

const ARCHIVED: HorizonStatus[] = ["archived"];

/** Tylko obszary i cele mają projekty; wizja i zasady są tłem. */
function isLinkable(level: HorizonLevel): boolean {
  return level === 2 || level === 3;
}

function projectCount(count: number): string {
  if (count === 1) return "1 projekt";
  const lastTwo = count % 100;
  const last = count % 10;
  const few = last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14);
  return `${count} ${few ? "projekty" : "projektów"}`;
}

/** Skąd brać priorytety: obszary, cele, wizja i zasady, z projektami przy obszarach i celach. */
export function HorizonsView() {
  const space = useSpaceParam();
  const { data, isLoading, error } = useHorizons(space);
  const archived = useHorizons(space, ARCHIVED);
  const create = useCreateHorizon();
  const [showForm, setShowForm] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  return (
    <div className="view">
      <button className="btn btn--primary btn--block" onClick={() => setShowForm(!showForm)}>
        {showForm ? "Anuluj" : "+ Nowy horyzont"}
      </button>

      {showForm && (
        <HorizonForm
          defaultSpace={space ?? null}
          busy={create.isPending}
          error={create.error}
          submitLabel="Dodaj"
          onSubmit={(input) => create.mutate(input, { onSuccess: () => setShowForm(false) })}
        />
      )}

      {isLoading && <p className="muted">Ładowanie…</p>}
      {error && <p className="error">Nie udało się pobrać horyzontów.</p>}
      {data && data.length === 0 && (
        <p className="muted">
          Brak horyzontów. Zapisane obszary i cele pomagają agentowi ustalać priorytety.
        </p>
      )}

      {LEVELS.map(({ level, title }) => {
        const rows = (data ?? []).filter((item) => item.level === level);
        if (rows.length === 0) return null;
        return (
          <section key={level} className="group">
            <h3 className="group__title">
              {title} <span className="group__count">{rows.length}</span>
            </h3>
            <ul className="rows">
              {rows.map((item) => (
                <HorizonRow key={item.id} item={item} />
              ))}
            </ul>
          </section>
        );
      })}

      {(archived.data?.length ?? 0) > 0 && (
        <section className="group">
          <button className="btn btn--block" onClick={() => setShowArchived(!showArchived)}>
            {showArchived ? "Ukryj archiwum" : `Archiwum (${archived.data?.length})`}
          </button>
          {showArchived && (
            <ul className="rows">
              {archived.data?.map((item) => (
                <HorizonRow key={item.id} item={item} />
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

function HorizonRow({ item }: { item: Horizon }) {
  const update = useUpdateHorizon();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const linkable = isLinkable(item.level);
  const detail = useHorizonDetail(open && linkable ? item.id : null);
  const spaces = useSpaces();
  const spaceName = spaces.data?.find((space) => space.id === item.space_id)?.name;
  const archived = item.status === "archived";

  return (
    <li className="row row--block">
      <div className="row__head" onClick={() => setOpen(!open)} role="button" tabIndex={0}>
        <div className="row__main">
          <span className="row__title">
            {item.title}
            {archived && <span className="badge">archiwum</span>}
          </span>
          <span className="row__meta">
            {linkable && <span>{projectCount(item.project_count)}</span>}
            {item.target_date && <span>do {shortDate(item.target_date)}</span>}
            {spaceName && <span>{spaceName}</span>}
          </span>
        </div>
        <span className="row__chevron">{open ? "▾" : "▸"}</span>
      </div>

      {open && (
        <div className="drawer">
          {editing ? (
            <HorizonForm
              initial={item}
              busy={update.isPending}
              error={update.error}
              submitLabel="Zapisz"
              onCancel={() => setEditing(false)}
              onSubmit={(input) =>
                update.mutate({ id: item.id, input }, { onSuccess: () => setEditing(false) })
              }
            />
          ) : (
            <>
              {item.description && <p className="reference-text">{item.description}</p>}
              {detail.data && detail.data.projects.length > 0 && (
                <section className="project-section">
                  <h4 className="project-section__title">Projekty</h4>
                  <ul className="horizon-projects">
                    {detail.data.projects.map(({ project }) => (
                      <li key={project.id}>
                        {project.title}
                        {project.status === "someday" && <span className="badge">kiedyś</span>}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              <div className="project-actions">
                <button
                  className="btn"
                  onClick={() => {
                    update.reset();
                    setEditing(true);
                  }}
                >
                  Edytuj
                </button>
                <button
                  className="btn"
                  disabled={update.isPending}
                  onClick={() =>
                    update.mutate({
                      id: item.id,
                      input: { status: archived ? "active" : "archived" },
                    })
                  }
                >
                  {archived ? "Przywróć" : "Do archiwum"}
                </button>
              </div>
              {update.error && <p className="error">{(update.error as Error).message}</p>}
            </>
          )}
        </div>
      )}
    </li>
  );
}

function HorizonForm({
  initial,
  defaultSpace,
  busy,
  error,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial?: Horizon;
  defaultSpace?: string | null;
  busy: boolean;
  error: unknown;
  submitLabel: string;
  onSubmit: (input: HorizonInput) => void;
  onCancel?: () => void;
}) {
  const spaces = useSpaces();
  const initialSpace =
    spaces.data?.find((space) => space.id === initial?.space_id)?.slug ?? defaultSpace ?? "";
  const [level, setLevel] = useState<HorizonLevel>(initial?.level ?? 2);
  const [title, setTitle] = useState(initial?.title ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [targetDate, setTargetDate] = useState(initial?.target_date ?? "");
  // `null` = użytkownik nie ruszał pola; lista przestrzeni może przyjść po otwarciu formularza.
  const [spaceChoice, setSpaceChoice] = useState<string | null>(null);
  const space = spaceChoice ?? initialSpace;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim()) return;
    onSubmit({
      level,
      title: title.trim(),
      description: description.trim() || null,
      target_date: level === 3 ? targetDate || null : null,
      space: space || null,
    });
  }

  return (
    <form className="project-edit" onSubmit={handleSubmit}>
      <label className="field">
        <span className="field__label">Poziom</span>
        <select
          className="field__input"
          value={level}
          onChange={(e) => setLevel(Number(e.target.value) as HorizonLevel)}
        >
          {LEVELS.map((option) => (
            <option key={option.level} value={option.level}>
              {option.singular}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span className="field__label">Nazwa</span>
        <input
          className="field__input"
          value={title}
          maxLength={512}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
      </label>
      <label className="field">
        <span className="field__label">Opis</span>
        <textarea
          className="field__input"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>
      {level === 3 && (
        <label className="field">
          <span className="field__label">Data docelowa</span>
          <input
            className="field__input"
            type="date"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
          />
        </label>
      )}
      <label className="field">
        <span className="field__label">Przestrzeń</span>
        <select
          className="field__input"
          value={space}
          onChange={(e) => setSpaceChoice(e.target.value)}
        >
          <option value="">Wspólne</option>
          {spaces.data?.map((option) => (
            <option key={option.id} value={option.slug}>
              {option.name}
            </option>
          ))}
        </select>
      </label>
      <div className="clarify__buttons">
        <button className="btn btn--primary" type="submit" disabled={!title.trim() || busy}>
          {submitLabel}
        </button>
        {onCancel && (
          <button className="btn" type="button" onClick={onCancel}>
            Anuluj
          </button>
        )}
      </div>
      {Boolean(error) && <p className="error">{(error as Error).message}</p>}
    </form>
  );
}
